import { FormEvent, useCallback, useEffect, useState } from "react";

import { CoreApiError } from "../../core-api/api-client";
import { statusLabel } from "../../localization/zh-CN";
import { loadCommercialWorkspace } from "../commercial/api";
import type { Supplier } from "../commercial/types";
import { workforceApi, WorkforceApiError } from "../workforce/api";
import type { WorkforceMembership, WorkforceTeam } from "../workforce/api";
import {
  approveQuoteDelivery,
  createCaseQuote,
  createQuoteDelivery,
  decideCaseQuoteApproval,
  distributeInquiry,
  generateQuoteDocument,
  loadCaseLifecycle,
  loadInquiryCenter,
  queueSyntheticQuoteDelivery,
  saveSourcingDraft,
  submitQuoteApproval,
  submitSourcingResult,
} from "./api";
import type {
  CaseLifecycleDetail,
  FlowLane,
  InquiryCenterItem,
  InquiryRoutingDecision,
  InquirySavedView,
  ParallelDistributionRequest,
  SourcingDraftRequest,
} from "./types";
import "./core-business-flow.css";

const SAVED_VIEWS: Array<{ key: InquirySavedView; label: string }> = [
  { key: "ALL", label: "全部" },
  { key: "NEW", label: "新询单" },
  { key: "SCREENING", label: "待筛选" },
  { key: "UNASSIGNED", label: "待分发" },
  { key: "PARALLEL", label: "并行处理中" },
  { key: "BLOCKED", label: "阻塞与超时" },
  { key: "READY_TO_QUOTE", label: "待销售报价" },
  { key: "PENDING_APPROVAL", label: "待审批" },
  { key: "QUOTE_SENT", label: "已发送报价" },
  { key: "WAITING_CUSTOMER", label: "等待客户" },
  { key: "CLOSED", label: "已关闭" },
];

const LIFECYCLE_STAGES = [
  "收到询单",
  "筛选确认",
  "销售/查货分发",
  "并行处理中",
  "查货结果完成",
  "客户报价准备",
  "内部审批",
  "报价发送",
  "等待客户",
  "关闭",
];

const WORKSPACE_TABS = [
  "总览",
  "原始询单",
  "客户与需求",
  "分发与任务",
  "查货与供应商",
  "成本与定价",
  "客户报价",
  "审批与沟通",
  "时间线与审计",
] as const;
type WorkspaceTab = (typeof WORKSPACE_TABS)[number];

export function CoreBusinessFlowWorkspace({
  mode = "inquiries",
  initialCaseId,
}: {
  mode?: "inquiries" | "exceptions";
  initialCaseId?: string;
}) {
  const [view, setView] = useState<InquirySavedView>(
    mode === "exceptions" ? "BLOCKED" : "ALL",
  );
  const [search, setSearch] = useState("");
  const [risk, setRisk] = useState("");
  const [overdue, setOverdue] = useState(mode === "exceptions");
  const [data, setData] =
    useState<Awaited<ReturnType<typeof loadInquiryCenter>>>();
  const [detail, setDetail] = useState<CaseLifecycleDetail>();
  const [selected, setSelected] = useState<InquiryCenterItem>();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData(
        await loadInquiryCenter({
          view,
          search,
          risk: risk || undefined,
          overdue,
          sort: "UPDATED_DESC",
        }),
      );
    } catch (cause) {
      setError(describe(cause));
    } finally {
      setLoading(false);
    }
  }, [overdue, risk, search, view]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!initialCaseId) return;
    setBusy(true);
    void loadCaseLifecycle(initialCaseId)
      .then(setDetail)
      .catch((cause) => setError(describe(cause)))
      .finally(() => setBusy(false));
  }, [initialCaseId]);

  const openItem = async (item: InquiryCenterItem) => {
    setSelected(item);
    setError("");
    if (!item.caseId) return;
    setBusy(true);
    try {
      setDetail(await loadCaseLifecycle(item.caseId));
      window.history.pushState(
        null,
        "",
        "/?view=inquiry-center&case=" + encodeURIComponent(item.caseId),
      );
    } catch (cause) {
      setError(describe(cause));
    } finally {
      setBusy(false);
    }
  };

  if (detail) {
    return (
      <CaseLifecycleWorkspace
        detail={detail}
        busy={busy}
        error={error}
        notice={notice}
        onBusy={setBusy}
        onError={setError}
        onNotice={setNotice}
        onReload={async () =>
          setDetail(await loadCaseLifecycle(detail.header.caseId))
        }
        onClose={() => {
          setDetail(undefined);
          window.history.replaceState(null, "", "/?view=inquiry-center");
        }}
      />
    );
  }

  return (
    <section className="core-flow">
      <div className="core-flow-toolbar">
        <div>
          <h2>{mode === "exceptions" ? "审批与异常" : "询单中心"}</h2>
        </div>
        <button type="button" disabled={loading} onClick={() => void load()}>
          {loading ? "刷新中…" : "刷新"}
        </button>
      </div>
      {data?.readOnly ? (
        <Feedback kind="info" text="当前账号为只读视图，已隐藏无权限操作。" />
      ) : null}
      {notice ? <Feedback kind="success" text={notice} /> : null}
      {error ? <Feedback kind="error" text={error} retry={load} /> : null}

      <div className="core-flow-saved-views" aria-label="询单保存视图">
        {SAVED_VIEWS.map((item) => (
          <button
            type="button"
            className={view === item.key ? "is-active" : undefined}
            key={item.key}
            onClick={() => setView(item.key)}
          >
            {item.label}
            <span>{data?.views[item.key] ?? 0}</span>
          </button>
        ))}
      </div>
      <div className="core-flow-filters">
        <label>
          <span>搜索</span>
          <input
            aria-label="搜索业务编号、客户或产品"
            placeholder="业务编号、客户、CAS 或产品"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
        <label>
          <span>风险</span>
          <select
            value={risk}
            onChange={(event) => setRisk(event.target.value)}
          >
            <option value="">全部风险</option>
            <option value="CLEAR">风险已清除</option>
            <option value="REVIEW_REQUIRED">需要审核</option>
            <option value="CONFIRMED_RISK">确认高风险</option>
          </select>
        </label>
        <label className="core-flow-check">
          <input
            type="checkbox"
            checked={overdue}
            onChange={(event) => setOverdue(event.target.checked)}
          />
          <span>只看超时</span>
        </label>
      </div>
      <div className="core-flow-table-wrap">
        <table className="core-flow-table">
          <thead>
            <tr>
              <th>业务编号 / 客户</th>
              <th>产品与数量</th>
              <th>风险 / 优先级</th>
              <th>销售 / 查货</th>
              <th>并行任务状态</th>
              <th>阶段 / 下一动作</th>
              <th>SLA</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {data?.items.map((item) => (
              <tr key={item.inquiryId}>
                <td>
                  <strong>{item.caseNumber ?? item.inquiryNumber}</strong>
                  <small>{item.customerCandidate}</small>
                  <small>{item.country}</small>
                </td>
                <td>
                  <strong>{item.productSummary}</strong>
                  <small>{item.quantity}</small>
                </td>
                <td>
                  <Status value={item.risk} />
                  <small>{statusLabel(item.priority)}</small>
                </td>
                <td>
                  <span>销售：{item.salesOwner ?? "待分配"}</span>
                  <small>查货：{item.sourcingOwner ?? "待分配"}</small>
                </td>
                <td>
                  <span>销售：{statusLabel(item.salesStatus)}</span>
                  <small>查货：{statusLabel(item.sourcingStatus)}</small>
                </td>
                <td>
                  <strong>{item.stage}</strong>
                  <small>{item.nextAction}</small>
                  {item.duplicateCandidates.length ? (
                    <em>
                      发现 {item.duplicateCandidates.length}{" "}
                      条近期重复候选，需人工确认
                    </em>
                  ) : null}
                  {item.blocker ? <em>{item.blocker}</em> : null}
                </td>
                <td>
                  <time>{formatDate(item.slaAt)}</time>
                  {item.overdue ? <em>已超时</em> : null}
                </td>
                <td>
                  {item.caseId || !data.readOnly ? (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void openItem(item)}
                    >
                      {item.caseId ? "打开工作台" : "筛选与分发"}
                    </button>
                  ) : (
                    <span>等待管理员分发</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && !data?.items.length ? (
          <Empty text="调整筛选条件，或刷新获取最新状态。" />
        ) : null}
        {loading ? (
          <div className="core-flow-loading">正在加载询单中心…</div>
        ) : null}
      </div>
      {selected && !selected.caseId && !data?.readOnly ? (
        <DistributionDialog
          inquiry={selected}
          onClose={() => setSelected(undefined)}
          onSuccess={async (caseId, caseNumber) => {
            setSelected(undefined);
            setNotice(
              "分发成功，已创建 " +
                caseNumber +
                "，销售与查货任务已同时进入工作队列。",
            );
            await load();
            setDetail(await loadCaseLifecycle(caseId));
          }}
        />
      ) : null}
    </section>
  );
}

function DistributionDialog({
  inquiry,
  onClose,
  onSuccess,
}: {
  inquiry: InquiryCenterItem;
  onClose: () => void;
  onSuccess: (caseId: string, caseNumber: string) => Promise<void>;
}) {
  const [teams, setTeams] = useState<WorkforceTeam[]>([]);
  const [salesMembers, setSalesMembers] = useState<WorkforceMembership[]>([]);
  const [sourcingMembers, setSourcingMembers] = useState<WorkforceMembership[]>(
    [],
  );
  const [salesTeamId, setSalesTeamId] = useState("");
  const [salesUserId, setSalesUserId] = useState("");
  const [sourcingTeamId, setSourcingTeamId] = useState("");
  const [sourcingUserId, setSourcingUserId] = useState("");
  const [sourcingRequired, setSourcingRequired] = useState(
    inquiry.routing?.requiresSourcing ?? inquiry.risk !== "CONFIRMED_RISK",
  );
  const [priority, setPriority] = useState<
    ParallelDistributionRequest["priority"]
  >(inquiry.priority as ParallelDistributionRequest["priority"]);
  const [note, setNote] = useState("管理员确认销售与查货并行处理");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [idempotencyKey] = useState(
    "core-flow-dispatch-" + crypto.randomUUID(),
  );

  useEffect(() => {
    void workforceApi
      .teams()
      .then(({ items }) => {
        setTeams(items);
        setSalesTeamId(
          items.find((item) => item.teamType === "SALES")?.id ?? "",
        );
        setSourcingTeamId(
          items.find((item) =>
            ["OPERATIONS", "TECHNICAL_REVIEW"].includes(item.teamType),
          )?.id ?? "",
        );
      })
      .catch((cause) => setError(describe(cause)));
  }, []);
  useEffect(() => {
    if (!salesTeamId) return;
    void workforceApi.teamMembers(salesTeamId).then(({ items }) => {
      setSalesMembers(items);
      setSalesUserId(items.find(activeMember)?.userId ?? "");
    });
  }, [salesTeamId]);
  useEffect(() => {
    if (!sourcingTeamId) return;
    void workforceApi.teamMembers(sourcingTeamId).then(({ items }) => {
      setSourcingMembers(items);
      const eligible = items.filter(activeMember);
      setSourcingUserId(
        eligible.find(
          (item) => item.userId === inquiry.routing?.suggestedSourcingUserId,
        )?.userId ??
          eligible.find(
            (item) => item.userId !== inquiry.routing?.excludeSourcingUserId,
          )?.userId ??
          "",
      );
    });
  }, [inquiry.routing, sourcingTeamId]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    const now = Date.now();
    const body: ParallelDistributionRequest = {
      expectedVersion: inquiry.inquiryVersion,
      priority,
      note,
      salesTeamId,
      salesUserId,
      salesAcceptanceDueAt: new Date(now + 2 * 3_600_000).toISOString(),
      salesProcessingDueAt: new Date(now + 24 * 3_600_000).toISOString(),
      salesReason: note,
      sourcingRequired,
      ...(sourcingRequired
        ? {
            sourcingTeamId,
            sourcingUserId,
            sourcingAcceptanceDueAt: new Date(
              now + 4 * 3_600_000,
            ).toISOString(),
            sourcingProcessingDueAt: new Date(
              now + 48 * 3_600_000,
            ).toISOString(),
            sourcingStrategy: "管理员确认的确定性规则建议",
            sourcingReason: "同一案件内并行查货任务",
          }
        : {}),
    };
    try {
      const result = await distributeInquiry(
        inquiry.inquiryId,
        body,
        idempotencyKey,
      );
      await onSuccess(result.caseId, result.caseNumber);
    } catch (cause) {
      setError(describe(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="core-flow-modal" role="presentation">
      <form
        className="core-flow-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="distribution-title"
        onSubmit={(event) => void submit(event)}
      >
        <header>
          <div>
            <h3 id="distribution-title">分发询单</h3>
          </div>
          <button type="button" onClick={onClose} aria-label="关闭分发弹窗">
            ×
          </button>
        </header>
        <div className="core-flow-dialog-summary">
          <strong>{inquiry.inquiryNumber}</strong>
          <span>{inquiry.productSummary}</span>
          <span>{inquiry.customerCandidate}</span>
          <span>{statusLabel(inquiry.risk)}</span>
        </div>
        {inquiry.routing ? (
          <Feedback
            kind={inquiry.routing.requiresRiskReview ? "error" : "info"}
            text={routingGuidance(inquiry.routing)}
          />
        ) : null}
        {error ? <Feedback kind="error" text={error} /> : null}
        <div className="core-flow-form-grid">
          <label>
            <span>优先级</span>
            <select
              value={priority}
              onChange={(event) =>
                setPriority(
                  event.target.value as ParallelDistributionRequest["priority"],
                )
              }
            >
              <option value="LOW">低</option>
              <option value="NORMAL">普通</option>
              <option value="HIGH">高</option>
              <option value="URGENT">紧急</option>
            </select>
          </label>
          <label className="is-wide">
            <span>分发备注</span>
            <textarea
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </label>
          <AssignmentFieldset
            title="销售分配"
            teams={teams.filter((item) => item.teamType === "SALES")}
            members={salesMembers}
            teamId={salesTeamId}
            userId={salesUserId}
            onTeam={setSalesTeamId}
            onUser={setSalesUserId}
            sla="接受 SLA：2 小时 · 首次处理 SLA：24 小时"
          />
          <fieldset>
            <legend>查货分配</legend>
            <label className="core-flow-check">
              <input
                type="checkbox"
                checked={sourcingRequired}
                disabled={Boolean(inquiry.routing)}
                onChange={(event) => setSourcingRequired(event.target.checked)}
              />
              <span>需要普通查货</span>
            </label>
            {sourcingRequired ? (
              <AssignmentFields
                teams={teams.filter((item) =>
                  ["OPERATIONS", "TECHNICAL_REVIEW"].includes(item.teamType),
                )}
                members={sourcingMembers}
                teamId={sourcingTeamId}
                userId={sourcingUserId}
                onTeam={setSourcingTeamId}
                onUser={setSourcingUserId}
              />
            ) : (
              <small>仅在风险审核、重复无货或复用有效结果时关闭。</small>
            )}
            <small>接受 SLA：4 小时 · 完成 SLA：48 小时</small>
          </fieldset>
        </div>
        <footer>
          <button type="button" onClick={onClose} disabled={busy}>
            取消
          </button>
          <button
            type="submit"
            className="is-primary"
            disabled={busy || !salesTeamId || !salesUserId}
          >
            {busy ? "正在确认分发…" : "确认并行分发"}
          </button>
        </footer>
      </form>
    </div>
  );
}

function AssignmentFieldset({
  title,
  sla,
  ...props
}: {
  title: string;
  sla: string;
  teams: WorkforceTeam[];
  members: WorkforceMembership[];
  teamId: string;
  userId: string;
  onTeam: (value: string) => void;
  onUser: (value: string) => void;
}) {
  return (
    <fieldset>
      <legend>{title}</legend>
      <AssignmentFields {...props} />
      <small>{sla}</small>
    </fieldset>
  );
}

function AssignmentFields({
  teams,
  members,
  teamId,
  userId,
  onTeam,
  onUser,
}: {
  teams: WorkforceTeam[];
  members: WorkforceMembership[];
  teamId: string;
  userId: string;
  onTeam: (value: string) => void;
  onUser: (value: string) => void;
}) {
  return (
    <>
      <label>
        <span>团队</span>
        <select value={teamId} onChange={(event) => onTeam(event.target.value)}>
          {teams.map((team) => (
            <option value={team.id} key={team.id}>
              {team.name}
            </option>
          ))}
        </select>
      </label>
      <label>
        <span>负责人</span>
        <select value={userId} onChange={(event) => onUser(event.target.value)}>
          {members.filter(activeMember).map((member) => (
            <option value={member.userId} key={member.id}>
              {member.user?.displayName ?? member.userId}
            </option>
          ))}
        </select>
      </label>
    </>
  );
}

function CaseLifecycleWorkspace({
  detail,
  busy,
  error,
  notice,
  onBusy,
  onError,
  onNotice,
  onReload,
  onClose,
}: {
  detail: CaseLifecycleDetail;
  busy: boolean;
  error: string;
  notice: string;
  onBusy: (value: boolean) => void;
  onError: (value: string) => void;
  onNotice: (value: string) => void;
  onReload: () => Promise<void>;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<WorkspaceTab>("总览");
  const currentStage = Math.max(
    0,
    LIFECYCLE_STAGES.indexOf(detail.header.stage),
  );
  return (
    <section className="case-lifecycle">
      <div className="case-fixed-header">
        <button type="button" onClick={onClose}>
          ← 返回询单中心
        </button>
        <div className="case-header-main">
          <div>
            <span className="core-flow-eyebrow">
              {detail.header.inquiryNumber} · {detail.header.caseNumber}
            </span>
            <h2>{detail.header.product}</h2>
            <p>
              {detail.header.customer} · {detail.header.country} ·{" "}
              {detail.header.quantity ?? "数量待确认"}
            </p>
          </div>
          <div className="case-header-actions">
            <Status value={detail.header.risk} />
            <Status value={detail.header.priority} />
            <button
              type="button"
              disabled={busy}
              onClick={() => void onReload()}
            >
              {busy ? "刷新中…" : "刷新状态"}
            </button>
          </div>
        </div>
        <dl className="case-header-grid">
          <KeyValue label="当前阶段" value={detail.header.stage} />
          <KeyValue
            label="销售负责人"
            value={detail.lanes.sales.assignee ?? "待分配"}
          />
          <KeyValue
            label="查货负责人"
            value={detail.lanes.sourcing.assignee ?? "不需要 / 待分配"}
          />
          <KeyValue label="下一动作" value={detail.header.nextAction} />
          <KeyValue label="当前 SLA" value={formatDate(detail.header.slaAt)} />
          <KeyValue label="阻塞原因" value={detail.header.blocker ?? "无"} />
          <KeyValue
            label="最近更新"
            value={formatDate(detail.header.updatedAt)}
          />
        </dl>
      </div>
      {notice ? <Feedback kind="success" text={notice} /> : null}
      {error ? <Feedback kind="error" text={error} retry={onReload} /> : null}
      <div className="lifecycle-track" aria-label="案件生命周期">
        {LIFECYCLE_STAGES.map((stage, index) => (
          <button
            type="button"
            key={stage}
            className={
              index < currentStage
                ? "is-complete"
                : index === currentStage
                  ? detail.header.blocker
                    ? "is-current is-blocked"
                    : "is-current"
                  : undefined
            }
            onClick={() => setTab(stageTab(stage))}
          >
            <span>{index + 1}</span>
            <strong>{stage}</strong>
          </button>
        ))}
      </div>
      <div className="parallel-lanes">
        <LaneCard
          lane={detail.lanes.sales}
          busy={busy}
          onBusy={onBusy}
          onError={onError}
          onNotice={onNotice}
          onReload={onReload}
        />
        <LaneCard
          lane={detail.lanes.sourcing}
          busy={busy}
          onBusy={onBusy}
          onError={onError}
          onNotice={onNotice}
          onReload={onReload}
        />
      </div>
      <nav className="case-workspace-tabs" aria-label="案件功能区">
        {WORKSPACE_TABS.map((item) => (
          <button
            key={item}
            type="button"
            className={tab === item ? "is-active" : undefined}
            onClick={() => setTab(item)}
          >
            {item}
          </button>
        ))}
      </nav>
      <div className="case-workspace-panel">
        {tab === "总览" ? <Overview detail={detail} /> : null}
        {tab === "原始询单" ? (
          <RecordGrid value={detail.originalInquiry ?? {}} />
        ) : null}
        {tab === "客户与需求" ? (
          <RecordGrid
            value={{
              客户: detail.header.customer,
              联系人: detail.header.contact.name,
              邮箱: detail.header.contact.email,
              电话: detail.header.contact.phone,
              国家: detail.header.country,
              产品: detail.header.product,
              CAS: detail.header.cas,
              规格: detail.header.specification,
              数量: detail.header.quantity,
            }}
          />
        ) : null}
        {tab === "分发与任务" ? (
          <RecordList values={detail.tasks} empty="尚无任务记录。" />
        ) : null}
        {tab === "查货与供应商" ? (
          <SourcingWorkspace
            detail={detail}
            busy={busy}
            onBusy={onBusy}
            onError={onError}
            onNotice={onNotice}
            onReload={onReload}
          />
        ) : null}
        {tab === "成本与定价" ? (
          <div className="case-split">
            <section>
              <h3>不可变成本快照</h3>
              <RecordList
                values={detail.costSnapshots}
                empty="查货提交后生成，不允许修改或删除。"
              />
            </section>
            <section>
              <h3>定价交接</h3>
              <RecordList
                values={detail.pricingHandoffs}
                empty="尚未形成销售可用的定价交接。"
              />
            </section>
          </div>
        ) : null}
        {tab === "客户报价" ? (
          <CaseQuoteWorkspace
            detail={detail}
            busy={busy}
            onBusy={onBusy}
            onError={onError}
            onNotice={onNotice}
            onReload={onReload}
          />
        ) : null}
        {tab === "审批与沟通" ? (
          <CaseQuoteWorkspace
            detail={detail}
            busy={busy}
            onBusy={onBusy}
            onError={onError}
            onNotice={onNotice}
            onReload={onReload}
            approvalMode
          />
        ) : null}
        {tab === "时间线与审计" ? <Timeline detail={detail} /> : null}
      </div>
    </section>
  );
}

function LaneCard({
  lane,
  busy,
  onBusy,
  onError,
  onNotice,
  onReload,
}: {
  lane: FlowLane;
  busy: boolean;
  onBusy: (value: boolean) => void;
  onError: (value: string) => void;
  onNotice: (value: string) => void;
  onReload: () => Promise<void>;
}) {
  const accept = async () => {
    if (!lane.assignmentId || lane.version == null) return;
    onBusy(true);
    onError("");
    try {
      await workforceApi.accept(lane.assignmentId, lane.version);
      onNotice((lane.lane === "SALES" ? "销售" : "查货") + "任务已接受。");
      await onReload();
    } catch (cause) {
      onError(describe(cause));
    } finally {
      onBusy(false);
    }
  };
  return (
    <section>
      <header>
        <div>
          <span>{lane.lane === "SALES" ? "销售轨道" : "查货轨道"}</span>
          <h3>{lane.assignee ?? "不需要 / 待分配"}</h3>
        </div>
        <Status value={lane.status} />
      </header>
      <dl>
        <KeyValue label="团队" value={lane.team ?? "—"} />
        <KeyValue label="接受截止" value={formatDate(lane.acceptanceDueAt)} />
        <KeyValue label="完成截止" value={formatDate(lane.processingDueAt)} />
        <KeyValue label="下一动作" value={lane.nextAction} />
      </dl>
      {lane.canAccept && lane.assignmentId ? (
        <button type="button" disabled={busy} onClick={() => void accept()}>
          {busy ? "处理中…" : "接受任务"}
        </button>
      ) : null}
    </section>
  );
}

function CaseQuoteWorkspace({
  detail,
  busy,
  onBusy,
  onError,
  onNotice,
  onReload,
  approvalMode = false,
}: {
  detail: CaseLifecycleDetail;
  busy: boolean;
  onBusy: (value: boolean) => void;
  onError: (value: string) => void;
  onNotice: (value: string) => void;
  onReload: () => Promise<void>;
  approvalMode?: boolean;
}) {
  const source = detail.quoteSources?.[0];
  const handoff = asRecord(detail.pricingHandoffs[0]);
  const quote = asRecord(detail.customerQuotes[0]);
  const versions = Array.isArray(quote.versions) ? quote.versions : [];
  const version = asRecord(versions[0]);
  const approval = asRecord(version.approvalRequest);
  const delivery = detail.deliveries
    .map(asRecord)
    .find((item) => item.quoteVersionId === version.id);
  const [unitPrice, setUnitPrice] = useState(
    stringValue(handoff.suggestedPrice) || "100.00",
  );
  const [approvalReason, setApprovalReason] = useState("本地 UAT 审批确认");
  const [preparedDeliveryId, setPreparedDeliveryId] = useState(
    stringValue(delivery?.id),
  );
  const [preparedDocumentId, setPreparedDocumentId] = useState(
    stringValue(delivery?.quoteDocumentId),
  );

  const perform = async (work: () => Promise<unknown>, success: string) => {
    onBusy(true);
    onError("");
    try {
      await work();
      onNotice(success);
      await onReload();
    } catch (cause) {
      onError(describe(cause));
    } finally {
      onBusy(false);
    }
  };

  const create = (event: FormEvent) => {
    event.preventDefault();
    if (!source) return;
    const now = new Date();
    const validUntil = new Date(now.getTime() + 14 * 86_400_000);
    const fxRate = source.costCurrency === "CNY" ? "0.13888889" : "1";
    void perform(
      () =>
        createCaseQuote(
          {
            caseId: detail.header.caseId,
            quoteNumber: quoteNumberFor(detail.header.caseNumber),
            currency: "USD",
            validUntil: validUntil.toISOString(),
            paymentTerms: "PREPAID",
            deliveryTerms: "DAP · SYNTHETIC UAT",
            riskFlags:
              detail.header.risk === "CLEAR" ? [] : [detail.header.risk],
            customerClass: "SYNTHETIC_UAT",
            pricing: {
              costCurrency: source.costCurrency,
              fxRate,
              fxSourceReference: "LOCAL_UAT_FIXED_RATE",
              fxEffectiveAt: now.toISOString(),
              quoteDiscountRate: "0",
              freight: "0",
              insurance: "0",
              handling: "0",
              taxRate: "0",
              duty: "0",
              surcharge: "0",
              roundingScale: 2,
            },
            lines: [
              {
                productReference: source.productReference ?? undefined,
                description: source.description,
                quantity: source.quantity,
                unit: source.unit,
                unitPrice,
                supplierOfferLineId: source.id,
                lineDiscountRate: "0",
              },
            ],
          },
          `core-flow-quote-${crypto.randomUUID()}`,
        ),
      "客户报价草稿已创建，已生成不可变版本。",
    );
  };

  const prepareDelivery = async () => {
    const versionId = stringValue(version.id);
    const context = detail.deliveryContext;
    if (!versionId || !context) return;
    onBusy(true);
    onError("");
    try {
      const document = await generateQuoteDocument(
        versionId,
        `core-flow-document-${crypto.randomUUID()}`,
      );
      const prepared = await createQuoteDelivery(
        versionId,
        {
          documentId: document.id,
          mailboxId: context.mailboxId,
          conversationId: context.conversationId,
          sender: { email: context.sender },
          recipients: [{ email: context.recipient }],
          subject: `合成报价 ${stringValue(quote.quoteNumber)}`,
          customerMessage:
            "这是本地 UAT 合成报价，仅进入 Mail Sink，不会发送到外部网络。",
        },
        `core-flow-delivery-${crypto.randomUUID()}`,
      );
      setPreparedDocumentId(document.id);
      setPreparedDeliveryId(prepared.id);
      onNotice(
        "最终报价文件与本地邮件草稿已生成，需由另一名获授权人员确认收件人后发送。",
      );
      await onReload();
    } catch (cause) {
      onError(describe(cause));
    } finally {
      onBusy(false);
    }
  };

  if (!quote.id) {
    if (!detail.permissions.canCreateQuote) {
      return <Empty text="等待销售负责人创建客户报价。" />;
    }
    if (!source) {
      return <Empty text="需先提交查货结果并形成可报价的定价交接。" />;
    }
    return (
      <form className="core-flow-quote-form" onSubmit={create}>
        <h3>创建客户报价</h3>
        <Feedback
          kind="info"
          text="报价基于当前 Case 的不可变查货结果；供应商身份、原始成本和内部毛利不会进入客户文件。"
        />
        <RecordGrid
          value={{
            产品: source.productReference ?? source.description,
            数量: `${source.quantity} ${source.unit}`,
            交期: source.leadTimeDays ? `${source.leadTimeDays} 天` : "待确认",
            成本币种: source.costCurrency,
            有效期: formatDate(source.validUntil),
          }}
        />
        <label>
          <span>客户单价（USD）</span>
          <input
            required
            inputMode="decimal"
            pattern="\d+(?:\.\d{1,4})?"
            value={unitPrice}
            onChange={(event) => setUnitPrice(event.target.value)}
          />
        </label>
        <button className="is-primary" type="submit" disabled={busy}>
          {busy ? "正在创建…" : "创建报价草稿"}
        </button>
      </form>
    );
  }

  const quoteStatus = stringValue(version.status) || stringValue(quote.status);
  const approvalStatus = stringValue(approval.status);
  const versionId = stringValue(version.id);
  const quoteId = stringValue(quote.id);
  const versionNumber = numberValue(version.versionNumber);
  const approvalId = stringValue(approval.id);
  const readyDeliveryId = preparedDeliveryId || stringValue(delivery?.id);
  const readyDocumentId =
    preparedDocumentId || stringValue(delivery?.quoteDocumentId);
  return (
    <section className="core-flow-quote-closure">
      <header>
        <div>
          <span className="core-flow-eyebrow">Case 级报价闭环</span>
          <h3>{stringValue(quote.quoteNumber)}</h3>
        </div>
        <Status value={quoteStatus || approvalStatus} />
      </header>
      <RecordGrid
        value={{
          报价版本: `v${versionNumber}`,
          报价状态: statusLabel(quoteStatus),
          审批状态: approvalStatus ? statusLabel(approvalStatus) : "尚未提交",
          币种: version.currency,
          总额: version.total,
          有效期: version.validUntil,
          发送状态: delivery?.status ?? "尚未准备",
        }}
      />
      {approvalMode &&
      approvalStatus === "PENDING" &&
      detail.permissions.canApprove ? (
        <div className="core-flow-approval-actions">
          <label>
            <span>审批意见</span>
            <textarea
              value={approvalReason}
              onChange={(event) => setApprovalReason(event.target.value)}
            />
          </label>
          <div>
            {(["REQUEST_CHANGES", "REJECT", "APPROVE"] as const).map(
              (action) => (
                <button
                  key={action}
                  type="button"
                  disabled={busy || approvalReason.trim().length < 3}
                  onClick={() =>
                    void perform(
                      () =>
                        decideCaseQuoteApproval(
                          approvalId,
                          versionNumber,
                          action,
                          approvalReason.trim(),
                          `core-flow-approval-${crypto.randomUUID()}`,
                        ),
                      action === "APPROVE"
                        ? "报价已批准。"
                        : "审批决定已记录。",
                    )
                  }
                >
                  {action === "APPROVE"
                    ? "批准"
                    : action === "REJECT"
                      ? "拒绝"
                      : "退回修改"}
                </button>
              ),
            )}
          </div>
        </div>
      ) : null}
      {approvalMode &&
      stringValue(delivery?.status) === "DRAFT" &&
      detail.permissions.canApproveDelivery ? (
        <button
          type="button"
          disabled={busy}
          onClick={() =>
            void perform(
              () => approveQuoteDelivery(stringValue(delivery?.id)),
              "本地邮件收件人与最终报价文件已由独立操作者确认。",
            )
          }
        >
          确认报价邮件收件人
        </button>
      ) : null}
      {!approvalId &&
      quoteStatus === "DRAFT" &&
      detail.permissions.canCreateQuote ? (
        <button
          type="button"
          disabled={busy}
          onClick={() =>
            void perform(
              () =>
                submitQuoteApproval(
                  quoteId,
                  versionId,
                  versionNumber,
                  `core-flow-approval-request-${crypto.randomUUID()}`,
                ),
              "报价已提交审批。",
            )
          }
        >
          {busy ? "正在提交…" : "提交报价审批"}
        </button>
      ) : null}
      {quoteStatus === "APPROVED" && detail.permissions.canSendQuote ? (
        <div className="core-flow-send-actions">
          {!readyDeliveryId ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => void prepareDelivery()}
            >
              {busy ? "正在生成…" : "生成文件并预览邮件"}
            </button>
          ) : stringValue(delivery?.status) === "DRAFT" ? (
            <Feedback
              kind="info"
              text="等待另一名获授权人员确认收件人与最终报价文件。"
            />
          ) : (
            <>
              <Feedback
                kind="info"
                text={`发件人：${detail.deliveryContext?.sender ?? "本地邮箱"}；收件人：${detail.deliveryContext?.recipient ?? "合成客户"}；仅发送至本地 Mail Sink。`}
              />
              {readyDocumentId ? (
                <>
                  <a
                    href={`/api/v1/quote-documents/${encodeURIComponent(readyDocumentId)}/download`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    预览 / 下载受控 PDF
                  </a>
                  <a
                    href={`/api/v1/quote-documents/${encodeURIComponent(readyDocumentId)}/download-word`}
                  >
                    下载受控 Word
                  </a>
                </>
              ) : null}
              <button
                type="button"
                disabled={
                  busy ||
                  ["QUEUED", "FAKE_SENT", "SENT"].includes(
                    stringValue(delivery?.status),
                  )
                }
                onClick={() =>
                  void perform(
                    () =>
                      queueSyntheticQuoteDelivery(
                        readyDeliveryId,
                        `core-flow-send-${crypto.randomUUID()}`,
                      ),
                    "报价已进入本地合成发送队列。",
                  )
                }
              >
                {busy ? "正在发送…" : "执行合成发送"}
              </button>
            </>
          )}
        </div>
      ) : null}
    </section>
  );
}

function Overview({ detail }: { detail: CaseLifecycleDetail }) {
  return (
    <div className="case-split">
      <section>
        <h3>当前业务状态</h3>
        <RecordGrid
          value={{
            当前阶段: detail.header.stage,
            下一动作: detail.header.nextAction,
            阻塞原因: detail.header.blocker ?? "无",
            风险等级: statusLabel(detail.header.risk),
            优先级: statusLabel(detail.header.priority),
            当前SLA: formatDate(detail.header.slaAt),
          }}
        />
      </section>
      <section>
        <h3>UAT 安全边界</h3>
        <RecordGrid
          value={{
            合成数据: detail.header.syntheticSafety?.isSynthetic ? "是" : "否",
            禁止联系: detail.header.syntheticSafety?.doNotContact ? "是" : "否",
            外发模式: statusLabel(
              detail.header.syntheticSafety?.outboundMode ?? "UNKNOWN",
            ),
            真实邮件: "禁用",
            真实Provider: "禁用",
          }}
        />
      </section>
    </div>
  );
}

function SourcingWorkspace({
  detail,
  busy,
  onBusy,
  onError,
  onNotice,
  onReload,
}: {
  detail: CaseLifecycleDetail;
  busy: boolean;
  onBusy: (value: boolean) => void;
  onError: (value: string) => void;
  onNotice: (value: string) => void;
  onReload: () => Promise<void>;
}) {
  const lane = detail.lanes.sourcing;
  const existing = detail.sourcingResults[0];
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [supplierId, setSupplierId] = useState(
    String(existing?.supplierId ?? ""),
  );
  const [unitCost, setUnitCost] = useState(
    String(existing?.unitCost ?? "1200.00"),
  );
  const [currency, setCurrency] = useState(String(existing?.currency ?? "CNY"));
  const [leadTimeDays, setLeadTimeDays] = useState(
    Number(existing?.leadTimeDays ?? 14),
  );
  const [validUntil, setValidUntil] = useState(
    String(existing?.validUntil ?? futureDate(15)).slice(0, 10),
  );
  const [packaging, setPackaging] = useState(
    String(existing?.packaging ?? "标准化学试剂包装"),
  );
  const [deviation, setDeviation] = useState(
    String(existing?.technicalDeviation ?? "无"),
  );
  const [noSupplyReason, setNoSupplyReason] = useState(
    String(existing?.noSupplyReason ?? ""),
  );
  const [saved, setSaved] = useState<{
    id: string;
    lockVersion: number;
  } | null>(
    existing?.id && existing?.lockVersion
      ? { id: String(existing.id), lockVersion: Number(existing.lockVersion) }
      : null,
  );
  useEffect(() => {
    if (!detail.permissions.canWorkSourcing) return;
    void loadCommercialWorkspace()
      .then((workspace) => {
        setSuppliers(workspace.suppliers);
        setSupplierId((current) => current || workspace.suppliers[0]?.id || "");
      })
      .catch(() => undefined);
  }, [detail.permissions.canWorkSourcing]);

  const draftBody = (): SourcingDraftRequest => ({
    expectedVersion: saved?.lockVersion ?? Number(existing?.lockVersion ?? 1),
    supplierId: supplierId || null,
    productReference: detail.header.cas ?? detail.header.product,
    specification: detail.header.specification,
    quantity: quantityNumber(detail.header.quantity),
    unit: quantityUnit(detail.header.quantity),
    currency: currency || null,
    unitCost: unitCost || null,
    domesticFees: "0",
    packagingFees: "0",
    internationalFreight: "0",
    otherFees: "0",
    quantityBreaks: [],
    packaging,
    leadTimeDays,
    validUntil: new Date(validUntil + "T00:00:00.000Z").toISOString(),
    technicalDeviation: deviation,
    transportRestrictions: "SYNTHETIC UAT ONLY",
    notes: "SYNTHETIC / UAT / TEST ONLY",
    noSupplyReason: noSupplyReason || null,
    evidenceReferences: ["synthetic://core-flow/uat-evidence"],
    needsClarification: false,
  });
  const save = async () => {
    if (!lane.assignmentId) return;
    onBusy(true);
    onError("");
    try {
      const result = await saveSourcingDraft(
        lane.assignmentId,
        draftBody(),
        "sourcing-draft-" + crypto.randomUUID(),
      );
      setSaved({ id: result.id, lockVersion: result.lockVersion });
      onNotice("查货草稿已保存；尚未生成成本快照。");
      await onReload();
    } catch (cause) {
      onError(describe(cause));
    } finally {
      onBusy(false);
    }
  };
  const submit = async (outcome: "SUBMITTED" | "NO_SUPPLY") => {
    if (!lane.assignmentId || !saved) return;
    onBusy(true);
    onError("");
    try {
      await submitSourcingResult(
        lane.assignmentId,
        saved.id,
        saved.lockVersion,
        outcome,
        "sourcing-submit-" + saved.id + "-" + outcome,
      );
      onNotice(
        outcome === "SUBMITTED"
          ? "查货结果已提交，成本快照与定价交接已同时生成。"
          : "已记录无货结果并更新案件阻塞状态。",
      );
      await onReload();
    } catch (cause) {
      onError(describe(cause));
    } finally {
      onBusy(false);
    }
  };

  if (!detail.permissions.canWorkSourcing) {
    return (
      <RecordList
        values={detail.sourcingResults}
        empty="当前角色只能查看职责范围内的查货结果。"
      />
    );
  }
  if (!["ACCEPTED", "IN_PROGRESS"].includes(lane.status)) {
    return <Empty text="请先在上方查货轨道接受任务，再填写查货结果。" />;
  }
  return (
    <form
      className="sourcing-form"
      onSubmit={(event) => event.preventDefault()}
    >
      <h3>查货结果填报</h3>
      <div className="core-flow-form-grid">
        <label>
          <span>供应商</span>
          <select
            value={supplierId}
            onChange={(event) => setSupplierId(event.target.value)}
          >
            <option value="">请选择</option>
            {suppliers.map((supplier) => (
              <option key={supplier.id} value={supplier.id}>
                {supplier.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>币种</span>
          <select
            value={currency}
            onChange={(event) => setCurrency(event.target.value)}
          >
            <option value="CNY">CNY</option>
            <option value="USD">USD</option>
          </select>
        </label>
        <label>
          <span>单位成本</span>
          <input
            value={unitCost}
            onChange={(event) => setUnitCost(event.target.value)}
          />
        </label>
        <label>
          <span>交期（天）</span>
          <input
            type="number"
            min="0"
            value={leadTimeDays}
            onChange={(event) => setLeadTimeDays(Number(event.target.value))}
          />
        </label>
        <label>
          <span>有效期</span>
          <input
            type="date"
            value={validUntil}
            onChange={(event) => setValidUntil(event.target.value)}
          />
        </label>
        <label>
          <span>包装</span>
          <input
            value={packaging}
            onChange={(event) => setPackaging(event.target.value)}
          />
        </label>
        <label className="is-wide">
          <span>技术偏差</span>
          <textarea
            value={deviation}
            onChange={(event) => setDeviation(event.target.value)}
          />
        </label>
        <label className="is-wide">
          <span>无货原因（仅标记无货时必填）</span>
          <textarea
            value={noSupplyReason}
            onChange={(event) => setNoSupplyReason(event.target.value)}
          />
        </label>
      </div>
      <div className="sourcing-actions">
        <button type="button" disabled={busy} onClick={() => void save()}>
          {busy ? "保存中…" : "保存草稿"}
        </button>
        <button
          type="button"
          disabled={busy || !saved || !supplierId || !unitCost}
          className="is-primary"
          onClick={() => void submit("SUBMITTED")}
        >
          提交查货结果
        </button>
        <button
          type="button"
          disabled={busy || !saved || !noSupplyReason}
          className="is-danger"
          onClick={() => void submit("NO_SUPPLY")}
        >
          标记无货
        </button>
      </div>
    </form>
  );
}

function Timeline({ detail }: { detail: CaseLifecycleDetail }) {
  return (
    <div className="case-split">
      <section>
        <h3>业务时间线</h3>
        <ol className="case-timeline">
          {detail.timeline.map((event) => (
            <li key={event.id}>
              <span />
              <div>
                <strong>{statusLabel(event.eventType)}</strong>
                <time>{formatDate(event.occurredAt)}</time>
                <small>{safeSummary(event.payload)}</small>
              </div>
            </li>
          ))}
        </ol>
      </section>
      <section>
        <h3>审计记录</h3>
        {detail.permissions.canReadAudit ? (
          <ol className="case-timeline">
            {detail.audit.map((event) => (
              <li key={event.id}>
                <span />
                <div>
                  <strong>{statusLabel(event.action)}</strong>
                  <time>{formatDate(event.occurredAt)}</time>
                  <small>真实操作者：{event.actorUserId ?? "系统"}</small>
                </div>
              </li>
            ))}
          </ol>
        ) : (
          <Empty text="当前账号没有 audit.read 权限。" />
        )}
      </section>
    </div>
  );
}

function Feedback({
  kind,
  text,
  retry,
}: {
  kind: "success" | "error" | "info";
  text: string;
  retry?: () => void | Promise<void>;
}) {
  return (
    <div
      className={"core-flow-banner is-" + kind}
      role={kind === "error" ? "alert" : "status"}
    >
      <span>{text}</span>
      {retry ? (
        <button type="button" onClick={() => void retry()}>
          重试
        </button>
      ) : null}
    </div>
  );
}

function Status({ value }: { value: string }) {
  return (
    <span className={"core-flow-status is-" + tone(value)}>
      {statusLabel(value)}
    </span>
  );
}

function KeyValue({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function RecordList({
  values,
  empty,
}: {
  values: Array<Record<string, unknown>>;
  empty: string;
}) {
  return values.length ? (
    <div className="case-card-list">
      {values.map((value, index) => (
        <RecordGrid key={String(value.id ?? index)} value={value} />
      ))}
    </div>
  ) : (
    <Empty text={empty} />
  );
}

function RecordGrid({ value }: { value: Record<string, unknown> }) {
  return (
    <dl className="record-grid">
      {Object.entries(value)
        .filter(([, item]) => item !== undefined)
        .map(([key, item]) => (
          <div key={key}>
            <dt>{recordLabel(key)}</dt>
            <dd>{displayValue(item)}</dd>
          </div>
        ))}
    </dl>
  );
}

function Empty({ text }: { text: string }) {
  return (
    <div className="core-flow-empty">
      <strong>暂无记录</strong>
      <span>{text}</span>
    </div>
  );
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function stringValue(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function numberValue(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function quoteNumberFor(caseNumber: string): string {
  return `CQ-${caseNumber.replace(/[^A-Za-z0-9-]/g, "-")}`.slice(0, 64);
}

function activeMember(member: WorkforceMembership): boolean {
  return !["ON_LEAVE", "UNAVAILABLE"].includes(member.availabilityStatus);
}

function routingGuidance(routing: InquiryRoutingDecision): string {
  const labels: Record<InquiryRoutingDecision["scenario"], string> = {
    HIGH_RISK_REVIEW:
      "高风险或特殊产品：创建风险审核任务，不创建普通查货任务。",
    REPEATED_NO_SUPPLY: "存在多次无货记录：挂载历史结果，不自动重复查货。",
    RECENT_VALID_SUCCESS:
      "一周内存在仍有效的成功结果：复用受控定价交接，不新建查货任务。",
    RECENT_NO_SUPPLY: "一周内查货无果：必须改派其他查货人员并使用不同来源。",
    OLDER_SUCCESS:
      "一年内存在历史成功结果：建议原查货人员重新确认价格、交期和库存。",
    NEW_OR_STALE: "从未查过或结果已超过一年：需要新建普通查货任务。",
  };
  return `确定性分发规则：${labels[routing.scenario]}`;
}

function describe(cause: unknown): string {
  if (cause instanceof CoreApiError || cause instanceof WorkforceApiError) {
    if (cause.status === 401) return "会话已过期，请重新登录。";
    if (cause.status === 403) return "当前账号没有执行此操作的权限。";
    if (cause.status === 409)
      return "记录已被其他操作更新或命令已执行，请刷新核对后重试。";
    if (cause.status === 422)
      return "分发策略、目标成员或必填字段未通过校验，请检查后重试。";
    if (cause.status === 0) return "请求超时或网络中断，请刷新核对后重试。";
  }
  return "业务操作暂时失败，请刷新核对状态后重试。";
}

function tone(status: string): "green" | "orange" | "red" | "blue" {
  if (
    ["CLEAR", "COMPLETED", "APPROVED", "READY", "READY_TO_QUOTE"].includes(
      status,
    )
  )
    return "green";
  if (
    ["HIGH", "URGENT", "CONFIRMED_RISK", "FAILED", "OVERDUE"].includes(status)
  )
    return "red";
  if (
    [
      "WAITING_INTERNAL",
      "WAITING_APPROVAL",
      "REVIEW_REQUIRED",
      "ASSIGNED",
    ].includes(status)
  )
    return "orange";
  return "blue";
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  return new Intl.DateTimeFormat("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function stageTab(stage: string): WorkspaceTab {
  if (["收到询单", "筛选确认"].includes(stage)) return "原始询单";
  if (["销售/查货分发", "并行处理中"].includes(stage)) return "分发与任务";
  if (stage === "查货结果完成") return "成本与定价";
  if (stage === "客户报价准备") return "客户报价";
  if (["内部审批", "报价发送", "等待客户"].includes(stage)) return "审批与沟通";
  return "总览";
}

function recordLabel(key: string): string {
  const labels: Record<string, string> = {
    id: "记录 ID",
    sourceInquiryId: "询单编号",
    sourceSubmittedAt: "提交时间",
    productDisplayName: "产品名称",
    catalogNumber: "货号",
    casRaw: "CAS",
    quantityRaw: "数量",
    packageRaw: "包装",
    purityRaw: "规格",
    countryDeclared: "国家",
    contactName: "联系人",
    companyName: "公司",
    normalizedEmail: "邮箱",
    safeCommentExcerpt: "留言摘要",
    title: "标题",
    description: "描述",
    assigneeUserId: "负责人",
    dueAt: "截止时间",
    completedAt: "完成时间",
    status: "状态",
    version: "版本",
    lockVersion: "记录版本",
    createdAt: "创建时间",
    submittedAt: "提交时间",
  };
  return labels[key] ?? key.replaceAll("_", " ");
}

function displayValue(value: unknown): string {
  if (value === null || value === "") return "—";
  if (typeof value === "boolean") return value ? "是" : "否";
  if (typeof value === "string") {
    if (/^\d{4}-\d{2}-\d{2}T/.test(value)) return formatDate(value);
    if (/^[A-Z][A-Z0-9_]*$/.test(value)) return statusLabel(value);
    return value;
  }
  if (typeof value === "number")
    return new Intl.NumberFormat("zh-CN").format(value);
  return safeSummary(value);
}

function safeSummary(value: unknown): string {
  if (!value || typeof value !== "object") return String(value ?? "—");
  return Object.entries(value as Record<string, unknown>)
    .slice(0, 6)
    .map(([key, item]) => recordLabel(key) + "：" + String(item ?? "—"))
    .join(" · ");
}

function quantityNumber(value: string | null): string {
  return value?.replace(",", ".").match(/\d+(?:\.\d+)?/)?.[0] ?? "1";
}

function quantityUnit(value: string | null): string {
  return value?.replace(/[\d.,\s]/g, "").slice(0, 16) || "unit";
}

function futureDate(days: number): string {
  return new Date(Date.now() + days * 86_400_000).toISOString().slice(0, 10);
}
